import * as React from 'react'
import {ReactElement} from 'react'
import {Box} from '@mui/material'
import {
    NatGuestOwnershipTransferNewMemberInfo,
    NatGuestOwnershipTransferNewMemberLocationState,
} from './newMember/NatGuestOwnershipTransferNewMemberInterface'
import {NatOwnershipTransferNewMemberDetail} from '../repository/NatGetOwnershipTransferNewMember'
import {NatOwnershipTransfersRepo} from '../repository/NatOwnershipTransfersRepo'
import NatCondominiumRepo from '../../condominium/repository/NatCondominiumRepo'
import NatCurrentDateTimeRepo from '../../currentDateTime/repository/NatCurrentDateTimeRepo'
import {Nullable} from '../../../../app/TypeHelpers'
import {NatPutOwnershipTransferNewMemberResponse} from '../repository/NatPutNewMemberRequest'
import {BlockSpacer} from '../../../../styledComponents/ResponsiveConvenience'
import {DisplayItemDividerForPageTop} from '../../../../styledComponents/DisplayItem'
import NatGuestNewMemberConfirm from './newMember/NatGuestNewMemberConfirm'
import {FullWidthColumnFlexContainer} from '../../../../styledComponents/FlexComponents'
import {PageTitle} from '../../applications/common/resident/NatResidentApplicationsCommon'
import {GuestApplyButton, GuestBackButton, GuestPlainLinkStyledBackButton} from '../common/NatOwnershipTransfersCommon'
import {
    createGuestWithErrorHandlingPage,
    GuestWithErrorHandlingPagePropsSet,
} from '../../../../components/shared/hoc/createGuestWithErrorHandlingPage'
import {Location} from 'react-router-dom'

interface Props {
    condominiumCd: string
    applyId?: string
    ownershipTransfersRepo: NatOwnershipTransfersRepo
    condominiumRepo: NatCondominiumRepo
    currentDateTimeRepo: NatCurrentDateTimeRepo
    location: Location<NatGuestOwnershipTransferNewMemberLocationState>
}

interface States {
    newMemberDetail: NatGuestOwnershipTransferNewMemberInfo
    ownershipTransferDate: Nullable<string>
    isLoadCompleted: boolean
}

class NatGuestNewMemberConfirmPage
    extends React.Component<Props & GuestWithErrorHandlingPagePropsSet, States> {

    constructor(props: Props & GuestWithErrorHandlingPagePropsSet) {
        super(props)

        this.state = {
            newMemberDetail: {
                accessExpirationDate: '',
                outlineInfo: null,
                detailInfo: null,
            },
            ownershipTransferDate: null,
            isLoadCompleted: false,
        }

        this.handleRegisterButtonClick = this.handleRegisterButtonClick.bind(this)
        this.handleEditButtonOnClick = this.handleEditButtonOnClick.bind(this)
        this.handleBackLinkOnClick = this.handleBackLinkOnClick.bind(this)
    }

    async componentDidMount(): Promise<void> {
        await this.props.eventEmitter.showLoading()
        try {
            await this.getApplyInfo()
        } finally {
            this.props.eventEmitter.closeLoading()
            this.setState({
                    isLoadCompleted: true,
                },
            )
        }
    }

    async getApplyInfo(): Promise<void> {
        if (!this.props.applyId && !this.props.location.state?.inputHistory) {
            return
        }

        let newMemberInfo = null
        let ownershipTransferDate = null
        if (this.props.location.state && this.props.location.state.inputHistory) {
            // 引継情報を使用
            newMemberInfo = {...this.props.location.state.inputHistory}
            ownershipTransferDate = this.props.location.state.ownershipTransferDate
        } else if (this.props.applyId) {
            // 取得APIを呼んでServerからの取得情報を使用
            const apiResponse = await this.getOwnershipTransferNewMember(this.props.applyId)
            if (apiResponse) {
                newMemberInfo = {
                    accessExpirationDate: apiResponse.newMemberInfo.accessExpirationDate,
                    outlineInfo: {
                        familyName: apiResponse.newMemberInfo.familyName,
                        firstName: apiResponse.newMemberInfo.firstName,
                        familyNameKana: apiResponse.newMemberInfo.familyNameKana,
                        firstNameKana: apiResponse.newMemberInfo.firstNameKana,
                        mailAddress: apiResponse.newMemberInfo.mailAddress,
                        mailAddressConfirm: apiResponse.newMemberInfo.mailAddress,
                        sharer: apiResponse.newMemberInfo.sharer,
                        residentPlace: apiResponse.newMemberInfo.residentPlace,
                    },
                    detailInfo: {
                        moveInDate: apiResponse.newMemberInfo.moveInDate,
                        contactInformation: apiResponse.newMemberInfo.contactInformation,
                        beforeContactAddress: apiResponse.newMemberInfo.beforeContactAddress,
                        proxyContactAddress: apiResponse.newMemberInfo.proxyContactAddress,
                        rentExistence: apiResponse.newMemberInfo.rentExistence,
                        paymentExistence: apiResponse.newMemberInfo.paymentExistence,
                        paymentContactAddress: apiResponse.newMemberInfo.paymentContactAddress,
                        destinationExistence: apiResponse.newMemberInfo.destinationExistence,
                        destinationContactAddress: apiResponse.newMemberInfo.destinationContactAddress,
                        version: apiResponse.newMemberInfo.version,
                    },
                }
                ownershipTransferDate = apiResponse.applyInfo.ownershipTransferDate
            }
        }
        if (newMemberInfo) {
            this.setState({
                newMemberDetail: newMemberInfo,
                ownershipTransferDate: ownershipTransferDate,
            })
        }
    }

    async getOwnershipTransferNewMember(applyId: string): Promise<Nullable<NatOwnershipTransferNewMemberDetail>> {
        const {condominiumCd, ownershipTransfersRepo, eventEmitter} = this.props

        const response = await this.props.asyncCallFunction(
            ownershipTransfersRepo.getOwnershipTransferNewMember
                .bind(ownershipTransfersRepo, condominiumCd, applyId),
            (httpErrorOrNetworkError) => {
                eventEmitter.showHeadingAreaMessage(httpErrorOrNetworkError)
            },
        )
        return response ? response.details : response
    }

    async handleRegisterButtonClick(): Promise<void> {
        await this.props.eventEmitter.showLoading()
        const {ownershipTransfersRepo, condominiumCd, applyId} = this.props

        if (this.state.newMemberDetail.outlineInfo &&
            this.state.newMemberDetail.detailInfo) {
            const request = {
                ...this.state.newMemberDetail.outlineInfo,
                ...this.state.newMemberDetail.detailInfo,
                applyId: applyId ?? null,
                confirmed: true,
            }

            try {
                const response: Nullable<NatPutOwnershipTransferNewMemberResponse> =
                    await this.props.asyncCallFunction(
                        ownershipTransfersRepo.applyOwnershipTransferNewMember.bind(
                            ownershipTransfersRepo, condominiumCd, request),
                        () => {
                            this.props.navigate(`/guest/error`, {replace: true})
                        },
                    )
                if (response && response.requestError === null) {
                    // 提出完了画面
                    this.props.navigate(
                        `/guest/condominiums/${this.props.condominiumCd}/applications/ownership-transfers/new-member/complete`,
                        {
                            replace: true,
                            state: {
                                applyId: response.details.applyId,
                                accessExpirationDate: response.details.accessExpirationDate,
                            },
                        },
                    )
                }
            } finally {
                this.props.eventEmitter.closeLoading()
            }
        }
    }

    // 修正するボタンを押下
    handleEditButtonOnClick(): void {
        const locationState: NatGuestOwnershipTransferNewMemberLocationState = this.props.location.state
        this.props.navigate(
            `/guest/condominiums/${this.props.condominiumCd}/applications/ownership-transfers/new-member/detail/${this.props.applyId ?? ''}`,
            {
                replace: true,
                state: JSON.parse(JSON.stringify(locationState)),
            },
        )
    }

    async handleBackLinkOnClick(): Promise<void> {
        const locationState: NatGuestOwnershipTransferNewMemberLocationState = this.props.location.state
        if (this.props.location.state?.isApply) {
            this.props.navigate(
                `/guest/condominiums/${this.props.condominiumCd}/applications/ownership-transfers/new-member/detail/${this.props.applyId ?? ''}`,
                {
                    replace: true,
                    state: JSON.parse(JSON.stringify(locationState)),
                },
            )
        } else {
            const {condominiumCd, ownershipTransfersRepo, eventEmitter} = this.props
            await eventEmitter.showLoading()
            try {
                const accessKeyByLoginUser = await this.props.asyncCallFunction(
                    ownershipTransfersRepo.getAccessKeyByLoginUser.bind(ownershipTransfersRepo, condominiumCd),
                    (httpErrorOrNetworkError) => {
                        eventEmitter.showHeadingAreaMessage(httpErrorOrNetworkError)
                    },
                )
                const accessKey = accessKeyByLoginUser?.details?.accessKey ?? ''
                this.props.navigate(`/guest/mypage?accessKey=${accessKey}`, {replace: true})
            } finally {
                eventEmitter.closeLoading()
            }
        }
    }

    render(): Nullable<ReactElement> {
        if (!this.state.isLoadCompleted) {
            return null
        }
        return (
            <>
                {(this.props.location.state?.isApply ||
                        !this.props.location.state?.isCompleteFlow) &&
                    <GuestPlainLinkStyledBackButton onClick={this.handleBackLinkOnClick}>
                        戻る
                    </GuestPlainLinkStyledBackButton>
                }
                <BlockSpacer height={'15px'}/>
                <DisplayItemDividerForPageTop/>
                <BlockSpacer height={'30px'}/>
                <PageTitle>
                    申請内容の確認
                </PageTitle>
                <Box height={'20px'}/>
                <NatGuestNewMemberConfirm
                    {...this.state.newMemberDetail}
                    ownershipTransferDate={this.state.ownershipTransferDate}
                    applyId={this.props.applyId}
                    isApply={this.props.location.state?.isApply}
                />
                {this.props.location.state?.isApply &&
                    <>
                        <BlockSpacer height={'20px'}/>
                        <GuestApplyButton onClick={this.handleRegisterButtonClick}>
                            申請を提出する
                        </GuestApplyButton>
                        <Box height={'30px'}/>
                        <GuestBackButton
                            onClick={this.handleEditButtonOnClick}>
                            <FullWidthColumnFlexContainer>
                                <div>
                                    修正する
                                </div>
                            </FullWidthColumnFlexContainer>
                        </GuestBackButton>
                    </>
                }
                <Box height={'15px'}/>
            </>
        )
    }
}

export default createGuestWithErrorHandlingPage(NatGuestNewMemberConfirmPage)
